/**
 * Answers written under an older shape of a question, read as the shape it has
 * now — D-059 (2026-11-02), the gaps HCA Tallahassee exposed.
 *
 * The catalog's ids are stable so stored answers keep mapping across rewords
 * (catalog.ts header). G3 went from a `bool` ("Core drilling required?") to a
 * `flagCount` (Yes, then how many holes). Every document on a phone or in D1
 * before this build carries a `bool` under a G3 key. Left alone, the flagCount
 * control would not read it, so a surveyor's Yes would vanish.
 *
 * The rule:
 *
 *   Yes   → Yes with no count, which `isAnswered` calls unanswered: the surveyor
 *           has to count. Never an assumed count: that is the bug D-059 removes.
 *   No    → No. A No the form wrote stays in `defaultedAnswers`, nobody's answer.
 *
 * Applied on READ by the store (a Dexie `reading` hook on the surveys table),
 * so every door out of IndexedDB sees the current shape and the next write
 * persists it. Nothing is rewritten in bulk.
 *
 * Pure. Returns the very same object when there is nothing to do, so a hook
 * that runs on every read allocates nothing on the common path.
 */

import type { AnswerKey, AnswerValue, SurveyDocument } from './types';

/** The question an answer key names — the last `|` segment (`answerKey`). */
function questionIdOf(key: AnswerKey): string {
  const at = key.lastIndexOf('|');
  return at < 0 ? key : key.slice(at + 1);
}

/** Question ids whose stored `bool` reads as a `flagCount` now. */
const BOOL_TO_FLAG_COUNT: ReadonlySet<string> = new Set(['G3']);

export function migrateLegacyAnswers(doc: SurveyDocument): SurveyDocument {
  let answers: Record<AnswerKey, AnswerValue> | undefined;

  for (const [key, value] of Object.entries(doc.answers)) {
    if (value.kind !== 'bool' || !BOOL_TO_FLAG_COUNT.has(questionIdOf(key))) continue;
    answers ??= { ...doc.answers };
    answers[key] = { kind: 'flagCount', value: { flag: value.value === true, count: 0 } };
  }

  return answers ? { ...doc, answers } : doc;
}
